/**
 * 系统管理接口（认证 + RBAC）
 * 与后端接口一一对应
 */
import request from './request'

const prefix = '/api'

/**
 * POST 参数序列化
 * 后端用 @RequestParam 接收，需要把参数拼到 URL 上：
 *   /system/dept/delete?id=4
 *   /system/user/assignRoles?userId=4&roleIds=1,2
 *
 * 数组统一用逗号拼接：
 * Spring 对 @RequestParam List<Integer> 与 @RequestParam String 都能正确解析
 */
function paramsSerializer(params) {
  const search = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    // 数组：过滤空值后用逗号拼接，空数组传空串，保证参数存在
    if (Array.isArray(value)) {
      const list = value.filter(
        item => item !== undefined && item !== null && item !== ''
      )
      search.append(key, list.join(','))
      return
    }

    if (value === undefined || value === null) {
      // 显式传 undefined / null 时跳过，交由调用方决定是否补空串
      return
    }

    search.append(key, typeof value === 'object' ? JSON.stringify(value) : value)
  })

  return search.toString()
}

/**
 * POST + JSON Body
 * 用于 add / update / assign 这类接收 @RequestBody DTO 的接口
 */
function postBody(url, payload = {}) {
  return request({
    url: prefix + url,
    method: 'post',
    data: payload
  })
}

/**
 * POST + Query 参数
 * 用于 delete / logout 这类接收 @RequestParam 的接口：
 *   /system/dept/delete?id=4
 */
function postQuery(url, payload = {}) {
  return request({
    url: prefix + url,
    method: 'post',
    params: payload,
    paramsSerializer
  })
}

/**
 * POST + Query 参数 + JSON Body
 * 用于「一部分参数走 @RequestParam、一部分走 @RequestBody」的接口：
 *   assignRoles(@RequestParam Integer userId, @RequestBody List<Integer> roleIds)
 *   → /system/user/assignRoles?userId=4  body: [2]
 *
 * query 里也带一份数组，兼容后端把 List 声明成 @RequestParam 的情况
 */
function postHybrid(url, query = {}, body) {
  return request({
    url: prefix + url,
    method: 'post',
    params: query,
    paramsSerializer,
    ...(body !== undefined ? { data: body } : {})
  })
}

// ==============================
// 认证
// ==============================
export function login(data) {
  return postBody('/auth/login', data)
}

export function logout() {
  return postQuery('/auth/logout')
}

export function getUserInfo() {
  return request({
    url: prefix + '/auth/userInfo',
    method: 'get'
  })
}

// ==============================
// 用户管理
// ==============================
export function getUserList(params) {
  return request({
    url: prefix + '/system/user/list',
    method: 'get',
    params
  })
}

export function addUser(data) {
  return postBody('/system/user/add', data)
}

export function updateUser(data) {
  return postBody('/system/user/update', data)
}

export function deleteUser(data) {
  return postQuery('/system/user/delete', data)
}

/**
 * 用户分配角色
 * 后端签名：assignRoles(@RequestParam Integer userId, @RequestBody List<Integer> roleIds)
 * 所以 userId 走 query，roleIds 走 JSON body
 */
export function assignRoles({ userId, roleIds = [] }) {
  const ids = (Array.isArray(roleIds) ? roleIds : [roleIds])
    .filter(v => v !== undefined && v !== null && v !== '')
    .map(Number)

  return postHybrid(
    '/system/user/assignRoles',
    { userId, roleIds: ids },
    ids
  )
}

// ==============================
// 角色管理
// ==============================
export function getRoleList(params) {
  return request({
    url: prefix + '/system/role/list',
    method: 'get',
    params
  })
}

export function addRole(data) {
  return postBody('/system/role/add', data)
}

export function updateRole(data) {
  return postBody('/system/role/update', data)
}

export function deleteRole(data) {
  return postQuery('/system/role/delete', data)
}

/**
 * 角色分配菜单
 * 与 assignRoles 同构：roleId 走 query，menuIds 走 JSON body
 */
export function assignMenus({ roleId, menuIds = [] }) {
  const ids = (Array.isArray(menuIds) ? menuIds : [menuIds])
    .filter(v => v !== undefined && v !== null && v !== '')
    .map(Number)

  return postHybrid(
    '/system/role/assignMenus',
    { roleId, menuIds: ids },
    ids
  )
}

// ==============================
// 菜单管理
// ==============================
export function getMenuTree(params) {
  return request({
    url: prefix + '/system/menu/tree',
    method: 'get',
    params
  })
}

export function getMenuTreeByUserId(params) {
  return request({
    url: prefix + '/system/menu/treeByUserId',
    method: 'get',
    params
  })
}

export function addMenu(data) {
  return postBody('/system/menu/add', data)
}

export function updateMenu(data) {
  return postBody('/system/menu/update', data)
}

export function deleteMenu(data) {
  return postQuery('/system/menu/delete', data)
}

// ==============================
// 部门管理
// ==============================
export function getDeptTree(params) {
  return request({
    url: prefix + '/system/dept/tree',
    method: 'get',
    params
  })
}

export function addDept(data) {
  return postBody('/system/dept/add', data)
}

export function updateDept(data) {
  return postBody('/system/dept/update', data)
}

export function deleteDept(data) {
  return postQuery('/system/dept/delete', data)
}
